import { create } from 'zustand';
import type { Plant, PlantSpecies, User } from '@/types';
import * as api from '@/utils/api';

interface AppState {
  // User
  user: User | null;
  isLoadingUser: boolean;

  // Plants
  plants: Plant[];
  isLoadingPlants: boolean;

  // Species
  species: PlantSpecies[];
  isLoadingSpecies: boolean;

  // Unread messages
  unreadCounts: Record<string, number>;

  // Error handling
  error: string | null;

  // Actions
  fetchUser: () => Promise<void>;
  fetchPlants: () => Promise<void>;
  fetchUnreadCounts: () => Promise<void>;
  createPlant: (data: api.CreatePlantDto) => Promise<Plant>;
  updatePlant: (id: string, data: api.UpdatePlantDto) => Promise<void>;
  deletePlant: (id: string) => Promise<void>;
  waterPlant: (id: string) => Promise<void>;
  fetchSpecies: () => Promise<void>;
  syncPlant: (plant: Plant) => void;
  archivePlant: (id: string) => Promise<void>;
  restorePlant: (id: string) => Promise<void>;
  waterAllPlants: (plantIds: string[]) => Promise<void>;
  clearUnreadCount: (plantId: string) => void;
  clearError: () => void;
}

export const useStore = create<AppState>((set) => ({
  // Initial state
  user: null,
  isLoadingUser: false,
  plants: [],
  isLoadingPlants: false,
  species: [],
  isLoadingSpecies: false,
  unreadCounts: {},
  error: null,

  // Actions
  fetchUser: async () => {
    set({ isLoadingUser: true, error: null });
    try {
      const user = await api.getCurrentUser();
      set({ user, isLoadingUser: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch user',
        isLoadingUser: false
      });
    }
  },

  fetchPlants: async () => {
    set({ isLoadingPlants: true, error: null });
    try {
      const plants = await api.getPlants();
      set({ plants, isLoadingPlants: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch plants',
        isLoadingPlants: false
      });
    }
  },

  fetchUnreadCounts: async () => {
    try {
      const { byPlant } = await api.getUnreadCounts();
      set({ unreadCounts: byPlant });
    } catch (error) {
      // Silently fail — unread counts are non-critical
      console.error('Failed to fetch unread counts:', error);
    }
  },

  clearUnreadCount: (plantId) => {
    set((state) => ({
      unreadCounts: { ...state.unreadCounts, [plantId]: 0 },
    }));
  },

  createPlant: async (data) => {
    set({ error: null });
    try {
      const plant = await api.createPlant(data);
      set((state) => ({ plants: [plant, ...state.plants] }));
      return plant;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create plant',
      });
      throw error;
    }
  },

  updatePlant: async (id, data) => {
    set({ error: null });
    try {
      const plant = await api.updatePlant(id, data);
      set((state) => ({
        plants: state.plants.map((p) => (p.id === id ? plant : p)),
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update plant',
      });
      throw error;
    }
  },

  deletePlant: async (id) => {
    set({ error: null });
    try {
      await api.deletePlant(id);
      set((state) => ({
        plants: state.plants.filter((p) => p.id !== id),
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete plant',
      });
      throw error;
    }
  },

  waterPlant: async (id) => {
    set({ error: null });

    // Optimistic update: immediately mark plant as watered
    const previousPlants = useStore.getState().plants;
    set((state) => ({
      plants: state.plants.map((p) => {
        if (p.id !== id) return p;
        return {
          ...p,
          wateringStatus: { status: 'ok' as const, daysUntil: p.wateringStatus?.daysUntil ?? 7, message: 'Только что полито' },
          lastWateredAt: new Date().toISOString(),
        };
      }),
    }));

    try {
      await api.recordCareAction(id, { actionType: 'WATERING' });
      // Refresh all data in parallel to reconcile with server
      const [updatedPlant, user, { byPlant }] = await Promise.all([
        api.getPlant(id),
        api.getCurrentUser(),
        api.getUnreadCounts(),
      ]);
      set((state) => ({
        plants: state.plants.map((p) => (p.id === id ? updatedPlant : p)),
        user,
        unreadCounts: byPlant,
      }));
    } catch (error) {
      // Revert optimistic update on failure
      set({
        plants: previousPlants,
        error: error instanceof Error ? error.message : 'Failed to water plant',
      });
      throw error;
    }
  },

  fetchSpecies: async () => {
    set({ isLoadingSpecies: true, error: null });
    try {
      const species = await api.getSpecies();
      set({ species, isLoadingSpecies: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch species',
        isLoadingSpecies: false
      });
    }
  },

  syncPlant: (plant) => {
    set((state) => ({
      plants: state.plants.map((p) => (p.id === plant.id ? plant : p)),
    }));
  },

  archivePlant: async (id) => {
    set({ error: null });
    try {
      await api.archivePlant(id);
      set((state) => ({
        plants: state.plants.filter((p) => p.id !== id),
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to archive plant',
      });
      throw error;
    }
  },

  restorePlant: async (id) => {
    set({ error: null });
    try {
      await api.restorePlant(id);
      // Refresh plants list to include restored plant
      const plants = await api.getPlants();
      set({ plants });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to restore plant',
      });
      throw error;
    }
  },

  waterAllPlants: async (plantIds) => {
    set({ error: null });

    // Optimistic update: immediately mark all target plants as watered
    const previousPlants = useStore.getState().plants;
    const idsSet = new Set(plantIds);
    set((state) => ({
      plants: state.plants.map((p) => {
        if (!idsSet.has(p.id)) return p;
        return {
          ...p,
          wateringStatus: { status: 'ok' as const, daysUntil: p.wateringStatus?.daysUntil ?? 7, message: 'Только что полито' },
          lastWateredAt: new Date().toISOString(),
        };
      }),
    }));

    try {
      await api.waterBatch(plantIds);
      // Refresh all data to reconcile with server
      const [plants, user, { byPlant }] = await Promise.all([
        api.getPlants(),
        api.getCurrentUser(),
        api.getUnreadCounts(),
      ]);
      set({ plants, user, unreadCounts: byPlant });
    } catch (error) {
      // Revert optimistic update on failure
      set({
        plants: previousPlants,
        error: error instanceof Error ? error.message : 'Failed to water plants',
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));

export type { CreatePlantDto, UpdatePlantDto } from '@/utils/api';
